import { put, takeLatest } from "redux-saga/effects";
import {
  createRoleError,
  createRoleRequest,
  createRoleSuccess,
  createTenantRoleError,
  createTenantRoleRequest,
  createTenantRoleSuccess,
  deleteRoleError,
  deleteRoleRequest,
  deleteRoleSuccess,
  getRoleByIdError,
  getRoleByIdRequest,
  getRoleByIdSuccess,
  getRolesError,
  getRolesRequest,
  getRolesSuccess,
  getTenantRoleDefaultError,
  getTenantRoleDefaultRequest,
  getTenantRoleDefaultSuccess,
  getTenantRolesError,
  getTenantRolesRequest,
  getTenantRolesSuccess,
  makeRoleDefaultError,
  makeRoleDefaultRequest,
  makeRoleDefaultSuccess,
  makeTenantRoleDefaultError,
  makeTenantRoleDefaultRequest,
  makeTenantRoleDefaultSuccess,
  updateRoleError,
  updateRoleRequest,
  updateRoleSuccess
} from "../features/organizations/roles";
import { httpDelete, httpGet, httpPost, httpPut } from "../../services";

function* fetchOrgRoles(action) {
  try {
    const response = yield httpGet("/rbac/organization/roles", { id: action.payload });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getRolesSuccess(response));
  } catch (error) {
    yield put(getRolesError(error.message));
  }
}
function* fetchTenantRoles() {
  try {
    const response = yield httpGet("/rbac/tenant/roles", {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getTenantRolesSuccess(response));
  } catch (error) {
    yield put(getTenantRolesError(error.message));
  }
}

function* fetchRoleById(action) {
  try {
    const response = yield httpGet("/rbac/role", { id: action.payload });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);

    yield put(getRoleByIdSuccess(response));
  } catch (error) {
    yield put(getRoleByIdError(error.message));
  }
}

function* createOrgRole(action) {
  const { orgId, ...rest } = action.payload;
  try {
    const response = yield httpPost("/rbac/organization/role", { id: orgId }, rest);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);

    yield put(createRoleSuccess(response));
  } catch (error) {
    yield put(createRoleError(error.message));
  }
}

function* createTenantRole(action) {
  try {
    const response = yield httpPost("/rbac/tenant/role", {}, action.payload);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);

    yield put(createTenantRoleSuccess(response));
  } catch (error) {
    yield put(createTenantRoleError(error.message));
  }
}

function* updateRole(action) {
  const { Id, ...rest } = action.payload;
  try {
    const response = yield httpPut("/rbac/role", { id: Id }, rest);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);

    yield put(updateRoleSuccess(response));
  } catch (error) {
    yield put(updateRoleError(error.message));
  }
}

function* makeOrgRoleDefault(action) {
  const { orgId, roleId } = action.payload;
  try {
    const response = yield httpPut("/rbac/organization/role/default", { orgId: orgId, roleId: roleId });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    if (response.Policies.MemberPolicy?.DefaultMemberRole === roleId) {
      yield put(makeRoleDefaultSuccess(action.payload.roleId));
    }
  } catch (error) {
    yield put(makeRoleDefaultError(error.message));
  }
}

function* makeTenantRoleDefault(action) {
  try {
    const response = yield httpPut("/rbac/tenant/role/default", { id: action.payload });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    if (response.IsDefault) {
      yield put(makeTenantRoleDefaultSuccess(action.payload));
    }
  } catch (error) {
    yield put(makeTenantRoleDefaultError(error.message));
  }
}

function* getTenantRoleDefault() {
  try {
    const response = yield httpGet("/rbac/tenant/role/default", {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getTenantRoleDefaultSuccess(response));
  } catch (error) {
    yield put(getTenantRoleDefaultError(error.message));
  }
}

function* deleteRole(action) {
  try {
    const response = yield httpDelete("/rbac/role", { id: action.payload });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    if (response.IsDeleted) {
      yield put(deleteRoleSuccess(action.payload));
    }
  } catch (error) {
    yield put(deleteRoleError(error.message));
  }
}

export function* watchRoleSaga() {
  yield takeLatest(getRolesRequest.type, fetchOrgRoles);
  yield takeLatest(getRoleByIdRequest.type, fetchRoleById);
  yield takeLatest(createRoleRequest.type, createOrgRole);
  yield takeLatest(updateRoleRequest.type, updateRole);
  yield takeLatest(makeRoleDefaultRequest.type, makeOrgRoleDefault);
  yield takeLatest(deleteRoleRequest.type, deleteRole);
  yield takeLatest(getTenantRoleDefaultRequest.type, getTenantRoleDefault);
}

export function* watchTenantRoleSaga() {
  yield takeLatest(getTenantRolesRequest.type, fetchTenantRoles);
  yield takeLatest(createTenantRoleRequest.type, createTenantRole);
  yield takeLatest(makeTenantRoleDefaultRequest.type, makeTenantRoleDefault);
}

import {
  createPermissionError,
  createPermissionRequest,
  createPermissionSuccess,
  deletePermissionError,
  deletePermissionRequest,
  deletePermissionSuccess,
  getPermissionsError,
  getPermissionsRequest,
  getPermissionsSuccess,
  updatePermissionError,
  updatePermissionRequest,
  updatePermissionSuccess
} from "../features/organizations/permissions";

function* fetchPermissions() {
  try {
    const response = yield httpGet("/rbac/permission/all", {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getPermissionsSuccess(response));
  } catch (error) {
    yield put(getPermissionsError(error.message));
  }
}

// function* fetchPermissionById(action) {
//   try {
//     const response = yield httpGet("/permission", { id: action.payload });
//     if (response.ErrorCode) throw new Error(response.message);
//     yield put(getPermissionByIdSuccess(response));
//   } catch (error) {
//     yield put(getPermissionByIdError(error.message));
//   }
// }

function* createPermission(action) {
  try {
    const response = yield httpPost("/rbac/permission", {}, action.payload);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(createPermissionSuccess({ Data: response }));
  } catch (error) {
    yield put(createPermissionError(error.message));
  }
}

function* updatePermission(action) {
  const { ID, ...rest } = action.payload;
  try {
    const response = yield httpPut("/rbac/permission", { id: ID }, rest);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(updatePermissionSuccess({ Data: response }));
  } catch (error) {
    yield put(updatePermissionError(error.message));
  }
}

function* deletePermission(action) {
  try {
    const response = yield httpDelete("/rbac/permission", { id: action.payload });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    if (response.IsDeleted) {
      yield put(deletePermissionSuccess(action.payload));
    }
  } catch (error) {
    yield put(deletePermissionError(error.message));
  }
}

export function* watchPermissionSaga() {
  yield takeLatest(getPermissionsRequest.type, fetchPermissions);
  yield takeLatest(createPermissionRequest.type, createPermission);
  yield takeLatest(updatePermissionRequest.type, updatePermission);
  yield takeLatest(deletePermissionRequest.type, deletePermission);
}
